import { useId } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { STATES } from "@/consts/states";
import {
  MAX_LICENSE_NUMBER_LENGTH,
  MAX_PREPARATION_LICENSES,
  type VerificationPreparation,
} from "./types";
import {
  isLicenseIncluded,
  type LicenseDraft,
  type LicenseSuggestion,
} from "./license-drafts";

const NO_LICENSE_SELECTED_MESSAGE =
  "Select at least one state license to verify.";

export function LicensePreparation({
  preparation,
  licenses,
  skippedStateCodes,
  noneSelected,
  suggestions,
  pending,
  duplicates,
  tooManyChanges,
  onChange,
  onStateIncludedChange,
}: {
  preparation: VerificationPreparation;
  licenses: LicenseDraft[];
  skippedStateCodes: string[];
  noneSelected: boolean;
  suggestions: LicenseSuggestion[];
  pending: boolean;
  duplicates: boolean;
  tooManyChanges: boolean;
  onChange: (licenses: LicenseDraft[]) => void;
  onStateIncludedChange: (change: {
    stateCode: string;
    included: boolean;
  }) => void;
}) {
  const formId = useId();
  function updateLicense(key: string, update: Partial<LicenseDraft>) {
    onChange(
      licenses.map((license) =>
        license.key === key ? { ...license, ...update } : license,
      ),
    );
  }
  return (
    <section
      className="space-y-3 border-t pt-4"
      aria-label="State license details"
    >
      <div className="space-y-1">
        <h3 className="font-medium">State licenses</h3>
        <p className="text-sm text-muted-foreground">
          Choose the states to verify in this run, and add any missing licenses.
          Changes to numbers are saved when you start verification.
        </p>
      </div>
      <div className="space-y-3">
        {licenses.map((license, index) => {
          const included = isLicenseIncluded({ license, skippedStateCodes });
          const stateName =
            STATES.find(([, code]) => code === license.stateCode)?.[0] ??
            license.stateCode;
          const licenseNumber = license.number.trim();
          const includeLabel = licenseNumber
            ? `Verify ${stateName} license ${licenseNumber} in this run`
            : `Verify ${stateName} license in this run`;
          const checkboxId = `${formId}-${license.key}-include`;
          const alternatives = suggestions.filter(
            (suggestion) =>
              suggestion.stateCode === license.stateCode &&
              !licenses.some(
                (draft) =>
                  draft.stateCode === suggestion.stateCode &&
                  draft.number.trim().toUpperCase() ===
                    suggestion.number.toUpperCase(),
              ),
          );
          const saved = preparation.licenses.find(
            (entry) => entry.credentialId === license.credentialId,
          );
          const unchanged = Boolean(
            saved &&
              license.stateCode === (saved.stateCode ?? "") &&
              license.number.trim() === (saved.number?.trim() ?? ""),
          );
          const correction = preparation.licenseCorrections.find(
            (entry) => entry.credentialId === license.credentialId,
          );
          const source = preparation.sources.find(
            (entry) =>
              entry.stateCode === license.stateCode &&
              entry.licenseNumbers.includes(saved?.number ?? ""),
          );
          return (
            <fieldset
              key={license.key}
              disabled={pending}
              className={`space-y-3 rounded-xl border p-3 ${
                included ? "" : "bg-muted/50 text-muted-foreground"
              }`}
            >
              <legend className="px-1 text-sm font-medium">
                License {index + 1}
              </legend>
              {license.credentialId && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={checkboxId}
                    checked={included}
                    aria-label={includeLabel}
                    onCheckedChange={(value) =>
                      onStateIncludedChange({
                        stateCode: license.stateCode,
                        included: value === true,
                      })
                    }
                  />
                  <Label htmlFor={checkboxId} className="cursor-pointer">
                    Verify in this run
                  </Label>
                  {!included && (
                    <span className="text-xs font-medium">
                      Skipped for this run
                    </span>
                  )}
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor={`${formId}-${license.key}-state`}>
                    State{" "}
                    <span className="text-xs text-muted-foreground">
                      Required
                    </span>
                  </Label>
                  <select
                    id={`${formId}-${license.key}-state`}
                    required
                    value={license.stateCode}
                    disabled={pending || Boolean(license.credentialId)}
                    onChange={(event) =>
                      updateLicense(license.key, {
                        stateCode: event.target.value,
                      })
                    }
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-70"
                  >
                    <option value="">Select state</option>
                    {license.stateCode &&
                      !STATES.some(
                        ([, code]) => code === license.stateCode,
                      ) && (
                        <option value={license.stateCode}>
                          {license.stateCode}
                        </option>
                      )}
                    {STATES.map(([name, code]) => (
                      <option key={code} value={code}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`${formId}-${license.key}-number`}>
                    License number{" "}
                    <span className="text-xs text-muted-foreground">
                      Required
                    </span>
                  </Label>
                  <Input
                    id={`${formId}-${license.key}-number`}
                    required
                    maxLength={MAX_LICENSE_NUMBER_LENGTH}
                    value={license.number}
                    disabled={!included}
                    onChange={(event) =>
                      updateLicense(license.key, { number: event.target.value })
                    }
                    placeholder="Include any letter prefix"
                  />
                </div>
              </div>
              {included && alternatives.length > 0 && (
                <div className="space-y-1.5 rounded-lg bg-blue-50 p-2 text-xs">
                  <p className="font-medium">
                    License numbers found for this NPI
                  </p>
                  <p>
                    {license.number
                      ? "Your current number is unchanged. Choose an alternative only if it is correct."
                      : "Choose the correct number or enter it above."}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {alternatives.map((suggestion) => (
                      <Button
                        key={suggestion.number}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          updateLicense(license.key, {
                            number: suggestion.number,
                          })
                        }
                      >
                        Use {suggestion.number}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              {unchanged &&
                included &&
                preparation.licenseIssues
                  .filter(
                    (issue) => issue.credentialId === license.credentialId,
                  )
                  .map((issue) => (
                    <p
                      key={issue.credentialId}
                      role="alert"
                      className="text-xs text-destructive"
                    >
                      {issue.message}
                    </p>
                  ))}
              {unchanged && included && correction && (
                <div className="space-y-1.5 rounded-lg bg-amber-50 p-2 text-xs text-amber-900">
                  <p>{correction.reason}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      updateLicense(license.key, { number: correction.number })
                    }
                  >
                    Use {correction.number}
                  </Button>
                </div>
              )}
              {license.credentialId ? (
                <p className="text-xs text-muted-foreground">
                  Saved to this organization. Edit or delete it in the
                  provider’s credentials.
                </p>
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs text-muted-foreground">
                    Not saved yet. Review before starting.
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove new license ${index + 1}`}
                    onClick={() =>
                      onChange(
                        licenses.filter((row) => row.key !== license.key),
                      )
                    }
                  >
                    <Trash2 className="mr-1.5 size-3.5" aria-hidden />
                    Remove
                  </Button>
                </div>
              )}
              {unchanged &&
                included &&
                !source &&
                !preparation.licenseIssues.some(
                  (issue) => issue.credentialId === license.credentialId,
                ) && (
                  <p className="text-xs text-amber-900">
                    Automatic verification is not supported for this license.
                    You can start the run; this license will be included for
                    manual review with licensing board links.
                  </p>
                )}
            </fieldset>
          );
        })}
      </div>
      {noneSelected && (
        <p role="alert" className="text-sm text-destructive">
          {NO_LICENSE_SELECTED_MESSAGE}
        </p>
      )}
      {duplicates && (
        <p role="alert" className="text-sm text-destructive">
          A license for this state is already listed. Correct the existing
          license instead of adding the same state again.
        </p>
      )}
      {tooManyChanges && (
        <p role="alert" className="text-sm text-destructive">
          You can save up to {MAX_PREPARATION_LICENSES} new or changed licenses
          at once. Remove extra suggestions before starting.
        </p>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={
          pending ||
          licenses.filter((license) => !license.credentialId).length >=
            MAX_PREPARATION_LICENSES
        }
        onClick={() =>
          onChange([
            ...licenses,
            { key: crypto.randomUUID(), number: "", stateCode: "" },
          ])
        }
      >
        <Plus className="mr-1.5 size-4" aria-hidden />
        Add state license
      </Button>
    </section>
  );
}
